/**
 * The vocabulary of chasing, in one place.
 *
 * Channels, outcomes and system events all get translated to English here and
 * nowhere else. The row chips, the drawer timeline and the log form all read
 * from this — so a channel is never called "WhatsApp" in one place and "WA" two
 * columns over.
 */

/**
 * The channels we actually use. Three.
 *
 * `OTHER` still exists server-side — the deprecated mark-asked and remind routes
 * log on it, and historical rows carry it — so `CHANNEL` below can still resolve
 * one for display. It is simply not offered, because nobody is chasing a client
 * by a fourth means and a picker option nobody picks is a picker that is harder
 * to read.
 */
const CHANNELS = [
  { key: 'WHATSAPP', label: 'WhatsApp', verb: 'WhatsApp message', icon: 'chat', cls: 'wa' },
  { key: 'EMAIL', label: 'Email', verb: 'email', icon: 'mail', cls: 'em' },
  { key: 'CALL', label: 'Call', verb: 'call', icon: 'phone', cls: 'cl' },
]

/**
 * The channels the row actually counts.
 *
 * WhatsApp and email — both real send paths now. Phone stays out: no call is
 * logged from this screen, so its chip would still be zero on every row of
 * the table, forever, which is furniture, not a fact.
 *
 * `CHANNELS` keeps all three: an attempt logged on any still resolves for
 * display in the thread, and putting a chip back is this list, not a rewrite.
 */
export const COUNTED_CHANNELS = CHANNELS.filter((c) => c.key === 'WHATSAPP' || c.key === 'EMAIL')

/**
 * What went out, in the words a person reading a log would want — not the
 * key a database column happens to use. The Log tab's whole point is a fast
 * glance, and `REQUEST_FIRST_WA` printed as-is defeats that as surely as no
 * label at all would.
 *
 * The last three are retired — nothing sends them any more (see the wrong-doc
 * flow, which stopped replying to a bad attachment entirely) — but an old row
 * still carries the key it was sent under, and it should still read sensibly
 * a year from now rather than fall back to the raw constant.
 */
export const TEMPLATE_LABEL = {
  // Retired template — kept only so messages sent with it still read properly in the thread.
  REQUEST_FIRST_WA: 'Asked for their statement',
  REQUEST_FIRST_EMAIL: 'Asked for their statement (email)',
  // Removed templates — labels kept so messages sent with them still read properly.
  REMINDER_PROMISED: 'Reminded — their promised date passed',
  REMINDER_PROMISED_TEST: 'Reminded — promised date passed (test)',
  SENT_BUT_NOT_ARRIVED: 'Asked them to resend',
  ASK_WHO_TO_CONTACT: 'Asked who to contact instead',
  UNCLEAR_NUDGE: 'Nudged back to the original ask',
  PROMISE_TOO_LONG: "Told them we'll check in within a week",
  WRONG_DOCUMENT: 'Told them: not a statement',
  WRONG_PERIOD_DOCUMENT: 'Told them: wrong period',
  UNREADABLE_DOCUMENT: "Told them: couldn't read it",
};


/**
 * Where the conversation stands, in the words a person would use.
 *
 * `attention` marks the states someone has to do something about, as opposed to
 * the ones where waiting is the correct action. That distinction is the whole
 * point of the column — "no reply yet" needs patience, "they replied" needs a
 * person, and both look identical on a status pill.
 */
export const CONVERSATION = {
  NOT_STARTED: { label: 'Not contacted', short: 'never asked', tone: 'p-bad', attention: true },
  /**
   * Synthetic — never a real `conversation.state` from the server. The
   * backend's state is derived purely from the attempt log, and there is no
   * attempt to derive anything from when a client has no contact at all.
   * `CollectionRow` computes this itself from the row's own WhatsApp/email
   * props and indexes it here directly, the one entry in this table nothing
   * server-side ever sets.
   */
  NO_CONTACT: { label: 'No contact on file', short: 'no contact', tone: 'p-bad', attention: true },
  AWAITING_REPLY: { label: 'No reply yet', short: 'no reply', tone: 'p-warn', attention: false },
  THEY_REPLIED: { label: 'They replied', short: 'replied', tone: 'p-info', attention: true },
  // Retired. Kept so a row saved before it went still reads as English.
  ASKED_A_QUESTION: { label: 'They asked us something', short: 'asked us', tone: 'p-info', attention: true },
  SAYS_SENT: { label: 'They say it was sent', short: 'says sent', tone: 'p-warn', attention: true },
  /**
   * The only state that is a stop rather than a step.
   *
   * Answering a disputed balance means quoting a figure, which needs a person —
   * so nothing about this row should read as routine.
   */
  DISPUTED: { label: 'Disputed a balance', short: 'disputed', tone: 'p-bad', attention: true },
  /**
   * The catch-all, in amber rather than red: red is kept for money. It means "we
   * saved what they said and nobody has worked out what it is", which is a
   * different urgency from a disputed balance.
   */
  NEEDS_REVIEW: { label: 'Needs reading', short: 'needs reading', tone: 'p-warn', attention: true },
  PROMISED: { label: 'They promised', short: 'promised', tone: 'p-warn', attention: false },
  PROMISE_LATE: { label: 'Promise date passed', short: 'promise late', tone: 'p-bad', attention: true },
  BAD_CONTACT: { label: 'Contact is wrong', short: 'contact dead', tone: 'p-bad', attention: true },
  REFUSED: { label: 'They refused', short: 'refused', tone: 'p-bad', attention: true },
  /*
   * Somebody told us to ask a different person, and their number is stored.
   *
   * Attention, but not alarm: nothing has gone wrong — the cycle has been reset
   * and the new contact has never been written to, so this row is work that is
   * ready to go rather than a problem to solve.
   */
  REDIRECTED: { label: 'Redirected — ask the new contact', short: 'redirected', tone: 'p-info', attention: true },
  /**
   * They said this number or person is wrong — or tapped "Ask someone else"
   * with no name — and we do not have anyone else to try yet.
   *
   * Amber, not red: it resolves on its own. We have already asked who to
   * contact automatically; this just means that hasn't been answered yet.
   * Silence after it clears the row back to an ordinary chase on the same
   * number, same as REDIRECTED clears once we've opened with a new one.
   */
  NEEDS_CONTACT: { label: 'Asked who to contact', short: 'needs contact', tone: 'p-warn', attention: true },
  RECEIVED: { label: 'Statement received', short: 'received', tone: 'p-ok', attention: false },
  // A rejected file used to hold its own state until we replied to it; we no
  // longer reply, so it no longer pauses the row at all. Kept alongside
  // DOC_REJECTED only for rows saved before that changed — nothing sets any
  // of these four any more.
  DOC_WRONG_PERIOD: { label: 'Wrong period', short: 'wrong period', tone: 'p-warn', attention: true },
  DOC_UNREADABLE: { label: "Can't open it", short: "can't open", tone: 'p-warn', attention: true },
  DOC_NOT_STATEMENT: { label: 'Not a statement', short: 'not a statement', tone: 'p-warn', attention: true },
  DOC_REJECTED: { label: 'Sent the wrong file', short: 'wrong file', tone: 'p-warn', attention: true },
}

/**
 * The CHASING column pill — eight buckets, deliberately coarser than the
 * full `CONVERSATION` vocabulary above. Refused, received, and promised are
 * named because they're exactly what was asked for; disputed and a passed
 * promise stay named too, on their own — both are a real reply needing a
 * person, and folding either into "no reply" would read as nothing having
 * happened on a row that actually needs attention. `BAD_CONTACT` and
 * `NEEDS_CONTACT` are the same category: a dead-end contact that will never
 * chase itself again automatically is exactly the kind of row this bucket
 * exists to not bury. Everything else — not yet contacted, awaiting reply,
 * an unclassified reply, redirected, a rejected file — reads as "No reply"
 * here.
 *
 * A display simplification only: `conversation.state` itself stays fully
 * granular (ActivityDrawer reads it directly for the detail view), and
 * neither the scheduler nor `nextTemplateFor` looks at this bucket at all.
 */
const CHASING_PILL = {
  REFUSED: { label: 'Refused', tone: 'p-bad', attention: true },
  RECEIVED: { label: 'Received', tone: 'p-ok', attention: false },
  PROMISED: { label: 'Promised', tone: 'p-warn', attention: false },
  DISPUTED: { label: 'Disputed', tone: 'p-bad', attention: true },
  PROMISE_LATE: { label: 'Promise late', tone: 'p-bad', attention: true },
  BAD_CONTACT: { label: 'Contact is wrong', tone: 'p-bad', attention: true },
  NEEDS_CONTACT: { label: 'Asked who to contact', tone: 'p-warn', attention: true },
}
const NO_REPLY_PILL = { label: 'No reply', tone: 'p-warn', attention: false }

/** The CHASING column's own {label, tone, attention} for one conversation state. */
export const chasingPillFor = (state) => CHASING_PILL[state] ?? NO_REPLY_PILL

/**
 * Chase counts that actually reached the client — every attempt per channel,
 * minus the ones that failed. What the chips show: a message that never got
 * through wasn't a chase the client saw.
 */
export function deliveredCounts(counts = {}, failed = {}) {
  const out = { ...counts }
  for (const [ch, n] of Object.entries(failed ?? {})) {
    if (typeof out[ch] === 'number') out[ch] = Math.max(0, out[ch] - n)
  }
  return out
}

/** Same rule as the backend's `parseEmailList`: at least one real address in the field. */
const hasUsableEmail = (raw) =>
  String(raw ?? '')
    .split(/[\s,;]+/)
    .some((part) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(part.trim()))

/** Can the chase reach this client at all — a WhatsApp number or a usable email? */
export const isReachable = (contact) => Boolean(contact?.whatsAppNo) || hasUsableEmail(contact?.email)
